import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { revealUp } from '../lib/anim';
import { MANIFESTO, STATS, PERSONAL_BIO } from '../data/portfolio';

function Chapter({ item, i }) {
  return (
    <motion.div
      variants={revealUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      custom={i}
      className="grid grid-cols-1 gap-4 border-t border-border py-10 md:grid-cols-12 md:gap-8 md:py-14"
    >
      <div className="md:col-span-3">
        <span className="font-mono text-sm text-muted-foreground">{item.n}</span>
      </div>
      <h3 className="font-display text-3xl font-medium leading-tight tracking-tight text-foreground md:col-span-5 md:text-4xl">
        {item.title}
      </h3>
      <p className="text-base leading-relaxed text-muted-foreground md:col-span-4">
        {item.body}
      </p>
    </motion.div>
  );
}

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['12%', '-12%']);

  return (
    <section
      id="about"
      ref={ref}
      className="relative mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <div className="md:col-span-4">
          <motion.div style={{ y }} className="md:sticky md:top-32">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              (About)
            </span>
            <motion.h2
              variants={revealUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.5 }}
              className="mt-6 font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-foreground md:text-5xl"
            >
              Design is how it works, felt in how it moves.
            </motion.h2>
          </motion.div>
        </div>

        <div className="md:col-span-8">
          <motion.div
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            data-testid="personal-bio"
            className="mb-4 border-t border-border py-10 md:py-14"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-8">
              <div className="md:col-span-3">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  Ciao,
                </span>
              </div>
              <p className="font-display text-2xl font-medium leading-[1.35] tracking-tight text-foreground md:col-span-9 md:text-3xl">
                <span className="text-foreground">{PERSONAL_BIO.split('.')[0]}.</span>
                <span className="text-muted-foreground">
                  {' ' + PERSONAL_BIO.split('.').slice(1).join('.').trim()}
                </span>
              </p>
            </div>
          </motion.div>

          {MANIFESTO.map((item, i) => (
            <Chapter key={item.n} item={item} i={i} />
          ))}

          <div className="mt-14 grid grid-cols-2 gap-8 border-t border-border pt-14 md:grid-cols-4">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                variants={revealUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.6 }}
                custom={i}
              >
                <div className="font-display text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
                  {s.value}
                </div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
